import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import type { SharePayload, VideoSettings } from '../lib/types';
import { qk } from '../queries/keys';
import { updateVideoSettings } from '../queries/share';

/**
 * Writing one recording's settings through, from wherever the owner changes
 * them: the Settings tab and the share window both use this, so a switch
 * flipped in one is already flipped in the other.
 *
 * Optimistic: the share payload in the cache is patched at once, and put back
 * from the server's answer, or refetched if the server said no.
 */
export function useSaveVideoSettings(videoId: string, shareId: string) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: Partial<VideoSettings>) => updateVideoSettings(videoId, patch),
    onMutate: (patch) => {
      setError(null);
      // Every share query for this recording, whoever is asking.
      queryClient.setQueriesData<SharePayload>({ queryKey: qk.share(shareId) }, (current) =>
        current?.settings ? { ...current, settings: { ...current.settings, ...patch } } : current,
      );
    },
    onSuccess: (next, patch) => {
      for (const [setting, value] of Object.entries(patch)) {
        track('Share Settings Changed', { video_id: shareId, setting, value });
      }
      queryClient.setQueriesData<SharePayload>({ queryKey: qk.share(shareId) }, (current) =>
        current
          ? {
              ...current,
              settings: next,
              viewer: current.viewer
                ? {
                    ...current.viewer,
                    captionsOn: next.captionsOn,
                    playbackRate: next.playbackRate,
                    commentsEnabled: next.commentsEnabled,
                    reactionsEnabled: next.reactionsEnabled,
                  }
                : current.viewer,
            }
          : current,
      );
    },
    onError: (cause) => {
      setError(errorMessage(cause));
      void queryClient.invalidateQueries({ queryKey: qk.share(shareId) });
    },
  });

  return { save, error };
}
